// frontend/src/pages/TeacherHome.jsx
import React, { useEffect, useState } from "react";
import SchoolIcon from "@mui/icons-material/School";
import { Link as RouterLink } from "react-router-dom";
import AtRiskSummaryCard from "../components/AtRiskSummaryCard";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CampaignIcon from "@mui/icons-material/Campaign";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
// TrendingDownIcon removed (low-engagement feature deleted)
import axios from "axios";
import { API_URL } from "../config";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function TeacherHome() {
  const theme = useTheme();

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });

  // カスタムクエリ関連の状態
  const [customQueryOpen, setCustomQueryOpen] = useState(false);
  const [customQuery, setCustomQuery] = useState("");
  const [customQueryLoading, setCustomQueryLoading] = useState(false);
  const [customQueryResponse, setCustomQueryResponse] = useState("");

  const TAB_KEYS = ["overview", "students", "contents", "activity"];
  const [tab, setTab] = useState(TAB_KEYS[0]);
  const handleTabChange = (_e, newValue) => setTab(newValue);

  const fetchDailySummary = async (isAutoRefresh = false) => {
    if (!isAutoRefresh) {
      setSummaryLoading(true);
      setSummaryError("");
    }
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API_URL}/reports/latest-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
      setSummaryError("");
      if (isAutoRefresh) setNotification({ open: true, message: "学習状況サマリーが自動更新されました。", severity: "info" });
    } catch (error) {
      console.error("日次サマリーの取得に失敗しました:", error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.overview || "サーバーエラーが発生しました。";
      if (!isAutoRefresh) {
        setSummaryError(errorMsg);
      }
    } finally {
      if (!isAutoRefresh) setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchDailySummary();
  }, []);

  const triggerReportGeneration = async () => {
    setIsGeneratingReport(true);
    setNotification({ open: false, message: "", severity: "info" });
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API_URL}/reports/trigger-generation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotification({ open: true, message: data.message || "レポート生成処理を開始しました。", severity: "success" });
      setTimeout(() => fetchDailySummary(true), 4000);
    } catch (error) {
      console.error("レポート生成トリガーAPIの呼び出しに失敗:", error);
      setNotification({ open: true, message: error.response?.data?.detail || "レポート生成の開始に失敗しました。", severity: "error" });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const triggerMonthlyReportGeneration = async () => {
    setIsGeneratingReport(true);
    setNotification({ open: false, message: "", severity: "info" });
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API_URL}/reports/generate-monthly`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotification({ open: true, message: data.message || "月次レポート生成を開始しました。", severity: "success" });
      // 月次レポートは集計に時間がかかる可能性があるため、少し余裕を持って再取得
      setTimeout(() => fetchDailySummary(true), 8000);
    } catch (error) {
      console.error("月次レポート生成APIの呼び出しに失敗:", error);
      setNotification({ open: true, message: error.response?.data?.detail || "月次レポート生成の開始に失敗しました。", severity: "error" });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleCloseNotification = (_, reason) => {
    if (reason === "clickaway") return;
    setNotification({ ...notification, open: false });
  };

  // カスタムクエリ送信処理
  const handleCustomQuerySubmit = async () => {
    if (!customQuery.trim() || customQuery.length < 5) {
      setNotification({ open: true, message: "質問を5文字以上入力してください。", severity: "warning" });
      return;
    }

    setCustomQueryLoading(true);
    setCustomQueryResponse("");
    
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_URL}/reports/custom-query`,
        { query: customQuery },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (data.success && data.response) {
        setCustomQueryResponse(data.response);
        setNotification({ open: true, message: "分析が完了しました。", severity: "success" });
      } else {
        setNotification({ open: true, message: "回答の取得に失敗しました。", severity: "error" });
      }
    } catch (error) {
      console.error("カスタムクエリの送信に失敗:", error);
      const errorMsg = error.response?.data?.detail || "クエリ処理中にエラーが発生しました。";
      setNotification({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setCustomQueryLoading(false);
    }
  };

  // カスタムクエリダイアログを開く
  const handleOpenCustomQuery = () => {
    setCustomQueryOpen(true);
    setCustomQuery("");
    setCustomQueryResponse("");
  };

  // カスタムクエリダイアログを閉じる
  const handleCloseCustomQuery = () => {
    setCustomQueryOpen(false);
    setCustomQuery("");
    setCustomQueryResponse("");
  };

  const renderOverview = () => (
    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: theme.palette.grey[100] }}>
      {summary?.overview || (summaryError ? "" : "概要データがありません。")}
    </Typography>
  );

  const renderStudentList = (label, list) => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ color: theme.palette.primary.light, mb: 1 }}>{label}</Typography>
      {list && list.length > 0 ? (
        list.map((s) => {
          // s.route_id はルーティング用の数値ID (例: 1, 2, 3)
          // s.id は表示用の文字列ID (例: s1234567)
          // s.name は学生のフルネーム
          // s.time はAIがフォーマットした時間文字列
          
          const studentHistoryUrl = s.route_id ? `/teacher/student/${s.route_id}/history` : null;

          return (
            <Typography key={s.id || s.route_id?.toString()} variant="body2" sx={{ color: theme.palette.grey[100], lineHeight: 1.8, mb: 0.5 }}>
              {studentHistoryUrl ? (
                <RouterLink 
                  to={studentHistoryUrl} 
                  style={{ 
                    color: theme.palette.info.light, 
                    textDecoration: 'underline',
                    fontWeight: '500' 
                  }}
                >
                  {s.id} ({s.name})
                </RouterLink>
              ) : (
                `${s.id} (${s.name || '詳細情報なし'})`
              )}
              {` – ${s.time || "時間情報なし"}`}
            </Typography>
          );
        })
      ) : (
        <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>該当なし</Typography>
      )}
    </Box>
  );
  
  const renderStudents = () => (
    <Box>
      {renderStudentList("📈 優秀者 (Top 5)", summary?.topStudents)} 
      {renderStudentList("🔍 学習不足 (要フォロー)", summary?.strugglingStudents)}
      {renderStudentList("🚀 最近伸びた学生", summary?.recentImprovers)}
    </Box>
  );

  const renderContents = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ color: theme.palette.primary.light, mb: 1 }}>📺 人気動画 (上位5件)</Typography>
      {summary?.popularVideos && summary.popularVideos.length > 0 ? (
        summary.popularVideos.map((v, idx) => (
          <Typography key={idx} variant="body2" sx={{ color: theme.palette.grey[100] }}>
            {`${idx + 1}. ${v.title || "タイトル不明"} – ${v.views !== undefined ? v.views : 'N/A'} views`}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>データなし</Typography>
      )}
    </Box>
  );

  const renderActivityChart = () => {
    const chartData = summary?.activityByHour && Array.isArray(summary.activityByHour) ? summary.activityByHour : [];
    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: -15 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[700]} />
          <XAxis dataKey="hour" tick={{ fill: theme.palette.common.white }} />
          <YAxis tick={{ fill: theme.palette.common.white }} />
          <Tooltip 
            contentStyle={{ backgroundColor: theme.palette.grey[800], border: 'none' }} 
            itemStyle={{ color: theme.palette.common.white }}
          />
          <Line type="monotone" dataKey="views" stroke={theme.palette.secondary.main} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: {xs: 2, md: 3}, backgroundColor: theme.palette.mode === "dark" ? "#1A1A1A" : "#212121", color: theme.palette.common.white, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: "bold", mb: 3, color: theme.palette.common.white }}>
          教員ダッシュボード
        </Typography>

        <AtRiskSummaryCard />

        <Box sx={{ mb: 3, p: 2, backgroundColor: theme.palette.mode === "dark" ? "#262626" : "#333333", color: theme.palette.common.white, borderRadius: 1, border: `1px solid ${theme.palette.grey[700]}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", mb: 0, color: theme.palette.common.white }}>
              <CampaignIcon sx={{ mr: 1, color: theme.palette.primary.main }} /> 本日の学習状況サマリー (AI分析)
            </Typography>
            <Button onClick={() => fetchDailySummary(false)} size="small" startIcon={<RefreshIcon />} disabled={summaryLoading || isGeneratingReport} variant="outlined" sx={{ color: theme.palette.grey[300], borderColor: theme.palette.grey[700], "&:hover": { borderColor: theme.palette.grey[500], backgroundColor: theme.palette.grey[800] } }}>
              {summaryLoading ? "更新中..." : "サマリー更新"}
            </Button>
          </Box>

          {summaryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress sx={{ color: theme.palette.common.white }} />
            </Box>
          ) : summaryError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{summaryError}</Alert>
          ) : !summary ? (
            <Typography sx={{textAlign: 'center', my: 2, color: theme.palette.grey[400]}}>サマリーデータを読み込めませんでした。</Typography>
          ): (
            <>
              <Tabs value={tab} onChange={handleTabChange} textColor="inherit" indicatorColor="secondary" variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="概要" value="overview" id="tab-overview" aria-controls="panel-overview" />
                <Tab label="学生ハイライト" value="students" id="tab-students" aria-controls="panel-students" />
                <Tab label="人気コンテンツ" value="contents" id="tab-contents" aria-controls="panel-contents" />
                <Tab label="視聴パターン" value="activity" id="tab-activity" aria-controls="panel-activity" />
              </Tabs>

              <Box role="tabpanel" hidden={tab !== "overview"} id="panel-overview" aria-labelledby="tab-overview" sx={{pt:2}}>
                {tab === "overview" && renderOverview()}
              </Box>
              <Box role="tabpanel" hidden={tab !== "students"} id="panel-students" aria-labelledby="tab-students" sx={{pt:2}}>
                {tab === "students" && renderStudents()}
              </Box>
              <Box role="tabpanel" hidden={tab !== "contents"} id="panel-contents" aria-labelledby="tab-contents" sx={{pt:2}}>
                {tab === "contents" && renderContents()}
              </Box>
              <Box role="tabpanel" hidden={tab !== "activity"} id="panel-activity" aria-labelledby="tab-activity" sx={{pt:2}}>
                {tab === "activity" && renderActivityChart()}
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ mt: 3, mb: 4, p: 2, textAlign: "center", backgroundColor: theme.palette.mode === "dark" ? "#262626" : "#333333", color: theme.palette.common.white, borderRadius: 1, border: `1px solid ${theme.palette.grey[700]}` }}>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: theme.palette.common.white, mb:1 }}>
            <RefreshIcon sx={{ mr: 1, color: theme.palette.secondary.main }} /> レポート生成・分析
          </Typography>
          
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Button variant="contained" color="secondary" startIcon={isGeneratingReport ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />} onClick={triggerReportGeneration} disabled={isGeneratingReport || summaryLoading} fullWidth>
                {isGeneratingReport ? "生成中..." : "週次レポート生成"}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" color="info" startIcon={<CalendarMonthIcon />} onClick={triggerMonthlyReportGeneration} disabled={isGeneratingReport || summaryLoading} fullWidth>
                月次レポート生成
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="outlined" color="success" startIcon={<SearchIcon />} onClick={handleOpenCustomQuery} disabled={isGeneratingReport || summaryLoading} fullWidth>
                カスタム分析
              </Button>
            </Grid>
          </Grid>
          
          <Typography variant="caption" display="block" sx={{ mt: 2, color: theme.palette.grey[400] }}>
            カスタム分析では、AIに自由に質問できます（例：「6月に最も見られたコンテンツは？」）
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6} md={5}>
            <Button component={RouterLink} to="/admin/at-risk" variant="contained" size="large" fullWidth startIcon={<AssessmentIcon />} sx={{
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              backgroundColor: '#c62828',
              '&:hover': { backgroundColor: '#8e0000' }
            }}>
              留年リスク（学生の提出状況）
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Button component={RouterLink} to="/teacher/students" variant="contained" size="large" fullWidth startIcon={<AssessmentIcon />} sx={{
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              backgroundColor: '#76b900',
              '&:hover': {
                backgroundColor: '#5c8f00'
              }
            }}>
              学生の視聴履歴一覧
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Button component={RouterLink} to="/teacher/video-upload" variant="contained" size="large" fullWidth startIcon={<UploadFileIcon />} sx={{ 
              py: 1.5, 
              textTransform: "none", 
              fontSize: "1rem",
              backgroundColor: '#76b900',
              '&:hover': {
                backgroundColor: '#5c8f00'
              }
            }}>
              動画のアップロード・編集
            </Button>
          </Grid>
           <Grid item xs={12} sm={6} md={4}> {/* 少し幅を調整するかもしれません */}
            <Button 
              component={RouterLink} 
              to="/teacher/subjects"  // ★新しい科目管理ページのパス（仮）
              variant="contained" 
              size="large" 
              fullWidth 
              startIcon={<SchoolIcon />} // ★アイコン例
              sx={{ 
                py: 1.5, 
                textTransform: "none", 
                fontSize: "1rem",
                backgroundColor: '#76b900',
                '&:hover': {
                  backgroundColor: '#5c8f00'
                }
              }}
            >
              科目とクラスコードの管理 {/* ★ボタン名例 */}
            </Button>
          </Grid>
        </Grid>

        {/* 先月の低エンゲージメント機能は削除されました */}

        <Box sx={{ mt: 4, p: 2, borderRadius: 1, backgroundColor: theme.palette.mode === "dark" ? "#262626" : "#333333", color: theme.palette.common.white, border: `1px solid ${theme.palette.grey[700]}` }}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.common.white }}>
            Co-Edu LMS へようこそ
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.grey[300] }}>
            このダッシュボードから、学生の学習進捗の確認や、教材動画の管理を行うことができます。
            上記のボタンやサマリー情報を活用して、日々の教育活動にお役立てください。
          </Typography>
        </Box>
      </Paper>

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: "100%",
            backgroundColor: notification.severity === "success" ? '#76b900' : undefined,
            '&:hover': {
              backgroundColor: notification.severity === "success" ? '#5c8f00' : undefined
            }
          }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* カスタムクエリダイアログ */}
      <Dialog 
        open={customQueryOpen} 
        onClose={handleCloseCustomQuery}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === "dark" ? "#1A1A1A" : "#212121",
            color: theme.palette.common.white
          }
        }}
      >
        <DialogTitle sx={{ color: theme.palette.common.white }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SearchIcon sx={{ mr: 1, color: theme.palette.success.main }} />
            カスタム分析クエリ
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.grey[300] }}>
            学習データについて自由に質問できます。AIが過去のデータを分析して回答します。
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.grey[400] }}>
            質問例：
          </Typography>
          <Box sx={{ mb: 2, pl: 2 }}>
            <Typography variant="body2" sx={{ color: theme.palette.grey[400] }}>
              • 6月に最も見られたコンテンツは？<br/>
              • 7月の映像視聴が少ない学生を列挙して<br/>
              • 先月の平均視聴時間は？<br/>
              • 最近1週間で視聴回数が増えた動画は？
            </Typography>
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="質問を入力してください"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            disabled={customQueryLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: theme.palette.common.white,
                '& fieldset': {
                  borderColor: theme.palette.grey[600],
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.grey[400],
                },
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.grey[400],
              }
            }}
          />
          
          {customQueryResponse && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.grey[900], borderRadius: 1, border: `1px solid ${theme.palette.grey[700]}` }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.success.main }}>
                分析結果：
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: theme.palette.grey[100] }}>
                {customQueryResponse}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCustomQuery} disabled={customQueryLoading}>
            閉じる
          </Button>
          <Button 
            onClick={handleCustomQuerySubmit} 
            variant="contained" 
            color="success"
            disabled={customQueryLoading || !customQuery.trim()}
            startIcon={customQueryLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
          >
            {customQueryLoading ? "分析中..." : "分析開始"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}